import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, FlaskConicalIcon } from 'lucide-react';
import { useDiscover, type VariantMode } from '../../contexts/DiscoverContext';
import { useDesignVariants } from '../../contexts/DesignVariantContext';
import { intervalLabel } from '../../data/designVariants';
import { cn } from '../../utils/cn';

/**
 * Development control for the presentation rotation.
 *
 * It exists so the two compositions and the handover between them can be
 * reviewed without waiting an hour. It is rendered only when
 * `discoverVariantConfig.showDeveloperControl` is on, and it is labelled as a
 * development affordance so nobody mistakes it for a product setting — students
 * are never asked to choose a composition.
 */
export function VariantDevControl() {
  const {
    variant,
    variantMode,
    setVariantMode
  } = useDiscover();
  const [open, setOpen] = useState(false);
  const {
    configFor
  } = useDesignVariants();
  const modes: VariantMode[] = ['auto', 'A', 'B'];
  const cadence = intervalLabel(configFor('DISCOVER').rotationIntervalMs);
  return <div className="fixed bottom-4 left-4 z-40 hidden lg:block" data-id="element-1697" data-mp="2muxdwhsnw09d">
      {open ? <div className="w-72 rounded-card border border-line bg-surface p-4 shadow-[0_16px_40px_rgba(15,23,42,0.18)]" data-id="element-1698" data-mp="2msue561pisrg">
          <div className="flex items-center justify-between gap-2" data-id="element-1699" data-mp="2cwrfia62dc99">
            <p className="text-body-sm font-semibold text-ink" data-id="element-1700" data-mp="2cykk7jx49mem">Development control</p>
            <button type="button" onClick={() => setOpen(false)} aria-label="Hide the development control" className="flex min-h-touch min-w-touch items-center justify-center rounded-lg text-ink-subtle hover:text-ink" data-id="element-1701" data-mp="3ajztzl7ndl1r">
              <ChevronDownIcon aria-hidden className="h-5 w-5" data-id="element-1702" data-mp="2vmftorlp4e1x" />
            </button>
          </div>
          <p className="mt-1 text-caption leading-5 text-ink-muted" data-id="element-1703" data-mp="1p7ybrrj7a043">
            Discover has two approved compositions over one shared state layer. Not shown to
            students.
          </p>

          <div className="mt-3 flex items-center gap-1 rounded-lg border border-line p-1" role="group" aria-label="Presentation variant" data-id="element-1704" data-mp="c9y44jinryp4">
            {modes.map(mode => <button key={mode} type="button" onClick={() => setVariantMode(mode)} aria-pressed={variantMode === mode} className={cn('min-h-touch flex-1 rounded-md text-body-sm font-medium capitalize transition-colors duration-150 ease-out', variantMode === mode ? 'bg-brand-700 text-white' : 'text-ink-muted hover:bg-canvas hover:text-ink')} data-id="element-1705" data-mp="2a76wa2pr5lc">
                {mode}
              </button>)}
          </div>

          <p className="mt-3 text-caption text-ink-subtle" data-id="element-1706" data-mp="35tid79iy3qio">
            Presenting <span className="font-semibold text-ink" data-id="element-1707" data-mp="3apr1bcf4eu1z">{variant}</span>. On auto it
            follows the published configuration — currently every {cadence}, set in Admin →
            Experience management.
          </p>
        </div> : <button type="button" onClick={() => setOpen(true)} className="flex min-h-touch items-center gap-2 rounded-full border border-line bg-surface px-3.5 text-body-sm font-medium text-ink-muted shadow-[0_10px_26px_rgba(15,23,42,0.14)] transition-colors duration-150 ease-out hover:text-ink" data-id="element-1708" data-mp="312dadwb30g23">
          <FlaskConicalIcon aria-hidden className="h-4 w-4" data-id="element-1709" data-mp="360omkdoun2nh" />
          Dev
          <ChevronUpIcon aria-hidden className="h-4 w-4" data-id="element-1710" data-mp="3jtnwm05v44nn" />
        </button>}
    </div>;
}